import React, { useState, useEffect } from "react";
import { Copy, Info, X, CheckCircle, ShoppingBag } from "lucide-react";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";

export const Offers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const response = await ApiService({
          method: "GET",
          endpoint: apiPaths.getActiveCoupons,
        });
        if (response.success) {
          setOffers(response.data);
        } else {
          setError("Failed to fetch offers");
        }
      } catch (err) {
        setError("Error connecting to the server");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, []);

  const handleCopyCode = (code) => {
    navigator.clipboard
      .writeText(code)
      .then(() => {
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
      })
      .catch((err) => console.error("Failed to copy: ", err));
  };

  const openTCModal = (offer) => {
    setSelectedOffer(offer);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      <h1 className="text-3xl font-bold text-center mt-8 mb-8">Special Offers</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {offers.map((offer) => (
          <div
            key={offer._id}
            className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200"
          >
            {/* Offer header */}
            <div className="bg-secondColor p-4 text-white ">
              <h2 className="text-xl font-semibold">{offer.offerName}</h2>
              <p className="text-sm mt-1">For {offer.userType} users</p>
            </div>

            {/* Offer body */}
            <div className="p-4">
              <div className="flex items-center justify-between bg-gray-100 p-3 rounded-lg mb-4">
                <span className="font-mono text-lg font-bold">
                  {offer.couponCode}
                </span>
                <button
                  onClick={() => handleCopyCode(offer.couponCode)}
                  className="flex items-center bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors"
                >
                  {copiedCode === offer.couponCode ? (
                    <>
                      <CheckCircle size={16} className="mr-1" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy size={16} className="mr-1" /> Copy
                    </>
                  )}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Limit:{" "}
                  {isNaN(offer.limit)
                    ? "unlimited"
                    : offer.limit
                    ? offer.limit
                    : 0}{" "}
                  uses
                </p>
                <button
                  onClick={() => openTCModal(offer)}
                  className="flex items-center text-sm text-blue-500 hover:text-blue-700"
                >
                  <Info size={16} className="mr-1" /> Know More
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* No offers message */}
      {offers.length === 0 && (
        <div className="text-center p-8 flex flex-col items-center justify-center space-y-4">
          <div className="bg-gray-100 p-4 rounded-full">
            <ShoppingBag className="h-8 w-8 text-gray-400" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">No active offers</h3>
            <p className="text-gray-500 mt-1">
              Check back later for new deals and promotions
            </p>
          </div>
        </div>
      )}

      {/* Terms & Conditions Modal */}
      {showModal && selectedOffer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-xl font-semibold">
                Offer Details: {selectedOffer.offerName}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 max-h-96 overflow-y-auto">
              <div className="mb-6">
                <h4 className="font-semibold text-lg mb-2">Coupon Code</h4>
                <div className="flex items-center justify-between bg-gray-100 p-3 rounded-lg">
                  <span className="font-mono text-lg font-bold">
                    {selectedOffer.couponCode}
                  </span>
                  <button
                    onClick={() => handleCopyCode(selectedOffer.couponCode)}
                    className="flex items-center bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors"
                  >
                    {copiedCode === selectedOffer.couponCode ? (
                      <>
                        <CheckCircle size={16} className="mr-1" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy size={16} className="mr-1" /> Copy
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-lg mb-2">Details</h4>
                <ul className="list-disc pl-5 space-y-2">
                  <li>
                    User Type:{" "}
                    <span className="font-medium">
                      {selectedOffer.userType}
                    </span>
                  </li>
                  <li>
                    Usage Limit:{" "}
                    <span className="font-medium">
                      {selectedOffer.limit} times
                    </span>
                  </li>
                </ul>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-lg mb-2">Highlights</h4>
                {selectedOffer.highlights &&
                selectedOffer.highlights.length > 0 ? (
                  <ul className="list-disc pl-5 space-y-2">
                    {selectedOffer.highlights.map((highlight, index) => (
                      <li key={index}>{highlight}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 italic">
                    No highlights available
                  </p>
                )}
              </div>

              <div>
                <h4 className="font-semibold text-lg mb-2">
                  Terms & Conditions
                </h4>
                {selectedOffer.termsAndConditions &&
                selectedOffer.termsAndConditions.length > 0 ? (
                  <ul className="list-disc pl-5 space-y-2">
                    {selectedOffer.termsAndConditions.map((term, index) => (
                      <li key={index}>{term}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 italic">
                    No terms and conditions specified
                  </p>
                )}
              </div>
            </div>

            <div className="border-t p-4 flex justify-end">
              <button
                onClick={() => setShowModal(false)}
                className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Offers;
